import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { Rive } from '@rive-app/webgl2';
import { interval, takeWhile, tap } from 'rxjs';
import { AudioTrackComponent } from 'shared/src/components/audio-track/audio-track.component';
import { CheckoutComponent, ProductView } from 'shared/src/components/checkout/checkout.component';
import { ModalComponent, ModalData } from 'shared/src/components/modal/modal.component';
import {
  UserReview,
  UserReviewComponent,
} from 'shared/src/components/user-review/user-review.component';
import { Placement } from 'shared/src/services/api.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { HOST_PROJECT_NAME, PROJECT_NAME, ProjectName, ProjectNames } from 'shared/src/tokens';
import { AnalyticsService } from '../../../../../shared/src/services/analytics.service';
import { StripeService } from '../../../../../shared/src/services/stripe.service';
import { fadeAnimation } from '../../animations/route-animations';
import { CompetitorFeatureRow } from '../../data/competitors';
import { highlightText } from '../../data/quiz';
import {
  PaywallAudioTrackSection,
  PaywallFaq,
  PaywallFeature,
  PaywallService,
} from '../../services/paywall.service';
import { NewsbreakService } from 'shared/src/services/newsbreak.service';

const strictPaywallProjects: readonly ProjectName[] = [
  ProjectNames.GOOGLE_FUNNEL,
  ProjectNames.SHORT_FUNNEL,
  // ProjectNames.KINDLE_FUNNEL, //TODO: maybe in future test it
];

const googleSourceProjects: readonly ProjectName[] = [
  ProjectNames.GOOGLE_FUNNEL,
  ProjectNames.SHORT_FUNNEL,
];

interface Placements {
  main: Placement;
  discount: Placement;
}

@Component({
  selector: 'app-funnel-paywall',
  imports: [
    CommonModule,
    MatDialogModule,
    MatExpansionModule,
    MatButtonModule,
    AudioTrackComponent,
    UserReviewComponent,
    CheckoutComponent,
  ],
  templateUrl: './paywall.component.html',
  styleUrls: ['./paywall.component.scss'],
  animations: [fadeAnimation],
})
export class PaywallComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('topCheckout') topCheckout: ElementRef;
  @ViewChildren('checkout') checkoutComponents: QueryList<CheckoutComponent>;
  @ViewChildren('audioTrackRef') audioTracks!: QueryList<AudioTrackComponent>;

  @ViewChild('beforeAnimation') beforeAnimation: ElementRef;
  @ViewChild('afterAnimation') afterAnimation: ElementRef;

  selectedProduct: ProductView;

  discountTitle: string = '60%';
  initialDiscountTitle: string = '';
  timer: string = '10:00';

  email: string | undefined;
  name: string | undefined;
  stripeCustomerId: string | undefined;

  title: string = $localize`:@@paywallDefaultTitle:Your personalized 3-month plan`;

  // Properties for contextual texts
  contextTitle: string = '';
  beforeText: string = '';
  afterText: string = '';

  purchaseButtonTitle = $localize`:@@continue:Continue`;
  discountReservedLabel = $localize`:@@paywallGiftLabelReserved:discount is reserved for:`;

  planCta: string = '';
  socialProofText: string = '';
  communityTitle: string = '';
  peechMembersPercentStat: string = '';

  highlights: string[] = [];
  features: PaywallFeature[] = [];
  faqs: PaywallFaq[] = [];
  audioTrackSections: PaywallAudioTrackSection[] = [];

  reviews: UserReview[];

  recentUsers: { name: string; timeAgo: string; id: number }[] = [];
  totalUsersThisHour: number = 0;

  isCompetitorInfoShown: boolean = false;
  comparisonTitle: string = '';
  comparisonRows: CompetitorFeatureRow[] = [];
  comparisonFootnote: string = '';

  isStrictPaywall: boolean = false; // not to sell productivity stuff etc

  placements: Placements = {
    main: 'growth_quiz',
    discount: 'growth_quiz_discount',
  };

  placement: Placement;

  private placementVariant: string | undefined;

  private userIdCounter: number = 0;
  private socialProofInterval: any;

  private readonly destroyRef = inject(DestroyRef);

  private beforeRiveInstance: Rive;
  private afterRiveInstance: Rive;

  constructor(
    private dialog: MatDialog,
    private paywallService: PaywallService,
    private userInfo: UserInfoService,
    private analyticsService: AnalyticsService,
    private stripeService: StripeService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private newsbreakService: NewsbreakService,
    @Inject(PROJECT_NAME) private projectName: ProjectName,
    @Inject(HOST_PROJECT_NAME) private hostProjectName: ProjectName
  ) {
    this.isStrictPaywall = strictPaywallProjects.includes(this.projectName);
    this.isCompetitorInfoShown = googleSourceProjects.includes(this.projectName);
  }

  ngOnInit(): void {
    this.reviews = this.paywallService.getReviews();
    this.title = this.paywallService.getTitle();
    this.planCta = this.paywallService.getPlanCta();
    this.highlights = this.paywallService.getHighlights();
    this.features = this.paywallService.getFeatures();
    this.faqs = this.paywallService.getFaqs();
    this.audioTrackSections = this.paywallService.getAudioTrackSections();
    this.communityTitle = this.paywallService.getCommunityTitle();
    this.peechMembersPercentStat = this.paywallService.getPeechMembersPercentStat();

    this.stripeCustomerId = this.userInfo.getStripeCustomerId();
    this.email = this.userInfo.getEmail();

    this.initPlacements();

    this.placement = this.placements.main;

    this.stripeService.preloadStripe();

    // Update contextual texts with a single call
    const textBundle = this.paywallService.getContextualTextBundle();

    this.contextTitle = textBundle.title;
    this.beforeText = textBundle.beforeText;
    this.afterText = textBundle.afterText;

    this.initializeRandomData();
    this.initCompetitorInfo();
  }

  ngAfterViewInit(): void {
    this.startTimer();
    this.startSocialProofSimulation();
    this.initAnimations();

    this.analyticsService.track('web_paywall_view', {
      before_text: this.beforeText,
      after_text: this.afterText,
      improvement_title: this.contextTitle,
      project: this.projectName,
      placement_variant: this.placementVariant,
    });
    //this.newsbreakService.addToCart();

    if (this.activatedRoute.snapshot.queryParams['fromCheckout']) {
      setTimeout(() => this.onPurchaseCanceled(), 800);
    }
  }

  ngOnDestroy(): void {
    this.beforeRiveInstance?.stop();
    this.beforeRiveInstance?.cleanup();
    this.afterRiveInstance?.stop();
    this.afterRiveInstance?.cleanup();

    if (this.socialProofInterval) {
      clearInterval(this.socialProofInterval);
    }
  }

  initAnimations(): void {
    if (this.isStrictPaywall) {
      return;
    }

    const beforeAnimation = this.beforeAnimation.nativeElement;
    const afterAnimation = this.afterAnimation.nativeElement;
    const { beforeFileName, afterFileName } = this.paywallService.getAnimationFiles();

    this.beforeRiveInstance = new Rive({
      src: `assets/rive/${beforeFileName}.riv`,
      canvas: beforeAnimation,
      autoplay: true,
      animations: ['Timeline 1'],
      onLoad: () => this.beforeRiveInstance?.resizeToCanvas(),
    });

    this.afterRiveInstance = new Rive({
      src: `assets/rive/${afterFileName}.riv`,
      canvas: afterAnimation,
      autoplay: true,
      animations: ['Timeline 1'],
      onLoad: () => this.afterRiveInstance?.resizeToCanvas(),
    });
  }

  initPlacements(): void {
    // Keyed on the host, not on projectName: the AppLovin build swaps projectName to the main or
    // Kindle funnel via `?funnel=`, and those visitors are still AppLovin traffic. Visitors who
    // hit the main or Kindle domain directly are served by their own builds and never land here.
    if (this.hostProjectName === ProjectNames.APPLOVIN_FUNNEL) {
      this.setPlacements('applovin');
      return;
    }

    if (googleSourceProjects.includes(this.projectName)) {
      this.setPlacements('google_quiz_2');
      return;
    }

    this.setPlacements('growth_quiz');
  }

  private setPlacements(placement: Placement): void {
    this.placementVariant = placement;
    this.placements = {
      main: placement,
      discount: `${placement}_discount` as Placement,
    };
  }

  initCompetitorInfo(): void {
    if (!this.isCompetitorInfoShown) {
      return;
    }

    const comparison = this.paywallService.getComparison();

    // Keep translations free of markup; apply highlight markup in code.
    const brand = $localize`:@@peechBrand:Peech`;
    const vs = $localize`:@@vs:vs`;
    const suffix = $localize`:@@paywallComparisonSuffix:- built for people`;

    this.comparisonTitle = `${highlightText(brand)} ${vs} ${comparison.opponent} ${suffix}`;
    this.comparisonRows = comparison.rows || [];
    this.comparisonFootnote = comparison.footnote || '';
  }

  payButtonClicked(): void {
    this.checkoutComponents.first.purchaseButtonClicked();
  }

  topButtonClicked(): void {
    //TODO: actually check for not only the first checkout on the page but for all of them
    if (this.isElementAtLeastVisible(this.topCheckout.nativeElement)) {
      this.payButtonClicked();
    } else {
      this.scrollToCheckout();
    }
  }

  handleDiscountTitle(title: string) {
    this.discountTitle = title;

    if (!this.initialDiscountTitle) {
      this.initialDiscountTitle = title;
    }
  }

  handlePurchaseButtonTitle(title: string) {
    this.purchaseButtonTitle = title;
  }

  shouldShowPromoCode(): boolean {
    return false;
  }

  getInsufficientFundsAction(): (() => void) | undefined {
    if (this.placement === this.placements.discount) {
      //nothing to offer already
      return;
    }

    return () => {
      if (!this.router.url.includes('paywall')) {
        this.router.navigate(['/paywall']);
      }

      setTimeout(() => {
        //TODO: maybe show another animation in this case
        //TODO: maybe introduce placement with even cheapest plans possible for this case
        this.switchToDiscount(
          $localize`:@@insufficientFundsDiscountMessage:We see that there are insufficient funds, but at Peech we are trying to support as many people as possible. That's why we are offering you an additional discount`
        );
      }, 1000);
    };
  }

  onPlay(currentTrack: AudioTrackComponent): void {
    this.analyticsService.track('web_audio_demo_played', { title: currentTrack.data.title });

    this.audioTracks.forEach(trackComponent => {
      if (trackComponent !== currentTrack) {
        trackComponent.pause();
      }
    });
  }

  onPurchaseCanceled() {
    if (this.placement === this.placements.discount) {
      //nothing to offer more
      return;
    }

    this.switchToDiscount(
      $localize`:@@purchaseCanceledDiscountMessage:We want you to succeed, which is why we are offering you an additional discount`
    );
  }

  private scrollToCheckout() {
    this.topCheckout.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private switchToDiscount(message: string) {
    this.dialog
      .open<ModalComponent, ModalData>(ModalComponent, {
        data: {
          title: $localize`:@@specialDiscountUnlockedTitle:Special Discount Unlocked`,
          message,
          okButtonTitle: $localize`:@@getMyDiscountCta:Get My Discount`,
          rive: {
            fileName: 'gift_box',
            animations: ['Timeline 1'],
            ratio: 1.1 / 1,
          },
        },
      })
      .afterClosed()
      .pipe(
        tap(() => {
          this.placement = this.placements.discount;
          this.analyticsService.track('web_discount_shown');
          this.scrollToCheckout();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private startTimer(): void {
    const secs = 10 * 60;

    interval(1000)
      .pipe(
        takeWhile(passedSecs => passedSecs < secs),
        tap(passedSecs => {
          const remainingSecs = secs - passedSecs - 1;
          const secsLeft = remainingSecs % 60;
          const leftSecsLabel = secsLeft > 9 ? `${secsLeft}` : `0${secsLeft}`;
          const minsLeft = Math.floor(remainingSecs / 60);

          this.timer = remainingSecs > 0 ? `${minsLeft}:${leftSecsLabel}` : '';
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private isElementAtLeastVisible(el: HTMLElement): boolean {
    const threshold = 0.7; // 70% by default
    const rect = el.getBoundingClientRect();

    // Viewport dimensions
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    // Element dimensions
    const elHeight = rect.height;
    const elWidth = rect.width;

    // If the element is entirely out of the viewport in any dimension, return false quickly
    if (rect.bottom < 0 || rect.top > windowHeight || rect.right < 0 || rect.left > windowWidth) {
      return false;
    }

    // Calculate the intersection coordinates
    const intersectionTop = Math.max(rect.top, 0);
    const intersectionBottom = Math.min(rect.bottom, windowHeight);
    const intersectionLeft = Math.max(rect.left, 0);
    const intersectionRight = Math.min(rect.right, windowWidth);

    // Intersection width & height
    const intersectionWidth = intersectionRight - intersectionLeft;
    const intersectionHeight = intersectionBottom - intersectionTop;

    // If there's no intersection area, it's not visible
    if (intersectionWidth <= 0 || intersectionHeight <= 0) {
      return false;
    }

    // Intersection area vs. total element area
    const intersectionArea = intersectionWidth * intersectionHeight;
    const elArea = elWidth * elHeight;

    // Compare ratio to threshold
    const ratio = intersectionArea / elArea;
    return ratio >= threshold;
  }

  private initializeRandomData(): void {
    if (this.isStrictPaywall) {
      return;
    }

    // Generate a random number between 300-500 for users who joined in the last hour
    this.totalUsersThisHour = Math.floor(Math.random() * 200) + 300;
    this.socialProofText = this.paywallService.getSocialProofText(this.totalUsersThisHour);

    // Initialize with a few users already showing
    this.addInitialUsers();
  }

  private addInitialUsers(): void {
    const firstNames = [
      'alex',
      'sam',
      'jamie',
      'casey',
      'jordan',
      'taylor',
      'morgan',
      'quinn',
      'charlie',
      'riley',
      'cameron',
      'avery',
      'dakota',
      'phoenix',
      'sage',
      'river',
    ];
    const lastInitials = [
      's',
      'k',
      'm',
      'd',
      't',
      'l',
      'r',
      'b',
      'h',
      'j',
      'n',
      'p',
      'w',
      'g',
      'c',
      'f',
    ];

    // Add 3-4 initial users with different times
    const initialUserCount = Math.floor(Math.random() * 2) + 3; // 3-4 users
    for (let i = 0; i < initialUserCount; i++) {
      const name = `${firstNames[Math.floor(Math.random() * firstNames.length)]}${lastInitials[Math.floor(Math.random() * lastInitials.length)]}**`;
      const minutesAgo = Math.floor(Math.random() * 10) + 1; // 1-10 minutes ago
      this.recentUsers.push({
        name,
        timeAgo: this.formatActivityMinutesAgo(minutesAgo),
        id: this.userIdCounter++,
      });
    }

    // Keep only the last 8 users visible
    if (this.recentUsers.length > 8) {
      this.recentUsers = this.recentUsers.slice(-8);
    }
  }

  private startSocialProofSimulation(): void {
    // Add a new user every 8-15 seconds
    this.socialProofInterval = setInterval(
      () => {
        this.addNewUser();
      },
      Math.floor(Math.random() * 5000) + 6000
    );
  }

  private addNewUser(): void {
    const firstNames = [
      'alex',
      'sam',
      'jamie',
      'casey',
      'jordan',
      'taylor',
      'morgan',
      'quinn',
      'charlie',
      'riley',
      'cameron',
      'avery',
      'dakota',
      'phoenix',
      'sage',
      'river',
    ];
    const lastInitials = [
      's',
      'k',
      'm',
      'd',
      't',
      'l',
      'r',
      'b',
      'h',
      'j',
      'n',
      'p',
      'w',
      'g',
      'c',
      'f',
    ];

    const name = `${firstNames[Math.floor(Math.random() * firstNames.length)]}${lastInitials[Math.floor(Math.random() * lastInitials.length)]}**`;

    // Add new user to the beginning of the array (most recent)
    this.recentUsers.unshift({
      name,
      timeAgo: this.formatActivityJustNow(),
      id: this.userIdCounter++,
    });

    // Update existing users' times
    this.updateUserTimes();

    // Keep only the last 8 users visible
    if (this.recentUsers.length > 8) {
      this.recentUsers = this.recentUsers.slice(0, 8);
    }

    // Increment total count
    this.totalUsersThisHour++;
  }

  private updateUserTimes(): void {
    this.recentUsers.forEach((user, index) => {
      if (index === 0) {
        user.timeAgo = this.formatActivityJustNow();
      } else if (index === 1) {
        user.timeAgo = this.formatActivityMinutesAgo(1);
      } else if (index < 5) {
        user.timeAgo = this.formatActivityMinutesAgo(index + 1);
      } else {
        user.timeAgo = this.formatActivityMinutesAgo(Math.floor(Math.random() * 10) + 6);
      }
    });
  }

  private formatActivityJustNow(): string {
    return $localize`:@@paywallActivityJustNow:just now`;
  }

  private formatActivityMinutesAgo(minutes: number): string {
    return $localize`:@@paywallActivityMinutesAgo:${minutes}:minutes:m ago`;
  }

  trackByUserId(index: number, user: { name: string; timeAgo: string; id: number }): number {
    return user.id;
  }
}
